import type { ReactNode } from 'react';

import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Skeleton } from '@/ui/base/skeleton';

import type { AlertaDestacado, ContagemPorStatus } from './contrato';
import type { EstadoDaColecao } from './useColecao';

/**
 * Peças que repetem entre as cinco telas de análises.
 *
 * Extraídas na primeira vez que apareceram duas vezes, como manda a regra 1 de
 * interface. `Secao` existe porque toda tela do módulo é uma pilha de blocos
 * titulados, e o título precisa ser cabeçalho de verdade — não `div` com
 * negrito — para o leitor de tela navegar por eles.
 */

interface SecaoProps {
  titulo: string;
  descricao?: string;
  children: ReactNode;
  /** Ações à direita do título (filtro, seletor de período). */
  acoes?: ReactNode;
}

export function Secao({ titulo, descricao, children, acoes }: SecaoProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <CardTitle className="text-base">{titulo}</CardTitle>
          {descricao ? <p className="text-sm text-muted-foreground">{descricao}</p> : null}
        </div>
        {acoes}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

interface GrupoProps {
  titulo: string;
  children: ReactNode;
  /** Filtro ou seletor à direita do título do grupo. */
  acoes?: ReactNode;
  /**
   * Título em caixa alta, como o protótipo desenha os grupos do catálogo
   * oficial de relatórios (`TERMOS DE ADESÃO`, `MULTAS`, `APURAÇÃO`).
   *
   * É estilo, não texto: o protótipo guarda `Termos de Adesão` e aplica
   * `text-transform: uppercase`, conferido em `getComputedStyle`. Por isso
   * entra como classe e não como `toUpperCase()` no conteúdo — o leitor de
   * tela e a busca continuam vendo o nome escrito por extenso.
   *
   * Fica opcional porque o outro grupo desta área, "Quadro de Desempenho
   * Operacional TEG", não é assim no protótipo.
   */
  caixaAlta?: boolean;
}

/**
 * Bloco que reúne várias seções sob um título só — no protótipo, "Quadro de
 * Desempenho Operacional TEG" agrupa três cartões, a matriz e o gráfico.
 *
 * O título é `h2` de verdade, e as `Secao` de dentro continuam sendo `h3` pelo
 * `CardTitle`: sem isso o leitor de tela veria seis blocos irmãos onde há um
 * grupo com filhos.
 */
export function Grupo({ titulo, children, acoes, caixaAlta = false }: GrupoProps) {
  return (
    <section className="space-y-4" aria-labelledby={`grupo-${titulo}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          id={`grupo-${titulo}`}
          className={
            caixaAlta
              ? 'text-sm font-semibold uppercase tracking-wide text-muted-foreground'
              : 'text-base font-semibold'
          }
        >
          {titulo}
        </h2>
        {acoes}
      </div>
      {children}
    </section>
  );
}

/** Tom do alerta por token semântico — nunca cor crua (regra 2 de interface). */
const TOM_DO_ALERTA: Record<AlertaDestacado['tom'], string> = {
  critico: 'border-destructive/40 bg-destructive/5',
  atencao: 'border-warning/40 bg-warning/5',
};

/** Cartões de alerta em destaque, acima dos blocos por status. */
export function CartoesDeAlerta({ itens }: { itens: readonly AlertaDestacado[] }) {
  if (itens.length === 0) {
    return null;
  }

  return (
    <dl className="grid gap-4 sm:grid-cols-2">
      {itens.map((item) => (
        <div key={item.rotulo} className={`rounded-lg border p-4 ${TOM_DO_ALERTA[item.tom]}`}>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {item.rotulo}
          </dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums">{item.valor}</dd>
          <p className="text-sm text-muted-foreground">{item.descricao}</p>
        </div>
      ))}
    </dl>
  );
}

/** Sigla da diretoria, como o protótipo a mostra ao lado do nome. */
export function SeloDeCodigo({ codigo }: { codigo: string }) {
  if (!codigo) {
    return null;
  }

  return <span className="mr-2 font-mono text-xs font-semibold text-primary">{codigo}</span>;
}

interface ListaDeContagensProps {
  itens: readonly ContagemPorStatus[];
  /** Rótulo acessível da lista, já que o título fica no cabeçalho da seção. */
  ariaLabel: string;
}

/** Bloco "X por status": par situação/quantidade, em lista de definição. */
export function ListaDeContagens({ itens, ariaLabel }: ListaDeContagensProps) {
  if (itens.length === 0) {
    return <EmptyState title="Sem contagem no período" variant="search" />;
  }

  return (
    <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label={ariaLabel}>
      {itens.map((item) => (
        <div key={item.status} className="rounded-lg border border-border bg-muted/40 p-3">
          <dt className="text-sm text-muted-foreground">{item.status}</dt>
          <dd className="text-2xl font-semibold tabular-nums">{item.quantidade}</dd>
        </div>
      ))}
    </dl>
  );
}

interface QuadroDeEstadoProps<T> {
  estado: EstadoDaColecao<T>;
  recarregar: () => void;
  /** Título do estado vazio, específico da tela. */
  tituloVazio: string;
  descricaoVazio: string;
  children: (dado: T) => ReactNode;
}

/**
 * Resolve os quatro estados num lugar só, para as cinco telas não repetirem o
 * mesmo `if`. O erro nunca mostra a mensagem técnica: ela fica no objeto de
 * estado (e no log), e a tela diz o que fazer — regra 8 de interface.
 */
export function QuadroDeEstado<T>({
  estado,
  recarregar,
  tituloVazio,
  descricaoVazio,
  children,
}: QuadroDeEstadoProps<T>) {
  if (estado.situacao === 'carregando') {
    return (
      <div className="space-y-4" aria-busy="true" aria-live="polite">
        <span className="sr-only">Carregando dados da tela.</span>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (estado.situacao === 'erro') {
    return (
      <ErrorState
        variant="load-failed"
        primaryAction={{ label: 'Tentar novamente', onClick: recarregar }}
      />
    );
  }

  if (estado.situacao === 'vazio') {
    return <EmptyState title={tituloVazio} description={descricaoVazio} variant="search" />;
  }

  return <>{children(estado.dado)}</>;
}
